import { RoundedFilledButton } from '@/components/buttons/Buttons';
import EmptyTable from '@/components/empty/EmptyTable';
import AutoCompleteSearch from '@/components/search-bar/AutocompleteSearchBar';
import { Table } from '@/components/table/Tables';
import RowExpansionTemplate from '@/components/table/table-content/TransactionRowExpansion';
import FilterPanel from '@/modules/home/components/filter-panel/FilterPanel';
import clsx from 'clsx';
import { Column } from 'primereact/column';
import {
	DataTable,
	DataTableStateEvent,
	DataTableValue,
	SortOrder,
	type DataTableExpandedRows,
	type DataTableValueArray,
} from 'primereact/datatable';
import { ProgressBar } from 'primereact/progressbar';
import { Tag } from 'primereact/tag';
import { Toolbar } from 'primereact/toolbar';
import { classNames } from 'primereact/utils';
import { type ReactNode, useEffect, useState } from 'react';
import { MdArrowDropDown, MdArrowRight, MdCloudDownload, MdFilterAlt, MdPinDrop, MdToday } from 'react-icons/md';
import styles from './table.module.css';
import { useQuery } from '@tanstack/react-query';
import { axiosInstance } from '@/services/axios.service';
import { copyWith, formatCurrency, GenericResponse, ListResponse } from '@/common/common_utils';
import { ENDPOINTS } from '@/constants/endpoints';
import { DateHelper } from '@/common/DateHelper';
import moment from 'moment';
import {
	buildPaginatorTemplate,
	PAGINATOR_CURRENT_REPORT,
	paginatorPassThrough,
} from '@/components/table/pagination/Paginator';
import { X4bProgramDto } from '@/modules/vehicle/engine/ActivityTypes';
import DateInput from '@/components/inputs/DateInput';
import { X4bPaymentDto, GetPaymentListRequest } from '@/modules/vehicle/engine/X4bPaymentDto';
import {
	convertPaymentStatus,
	expandUiStatusesForFilter,
	PAYMENT_STATUSES_UI,
	type PaymentStatusUi,
} from '@/modules/vehicle/engine/PaymentStatus';
import ActivitySearchAutocomplete from '@/components/search-bar/ActivitySearchAutocomplete';

interface Props {
	UserIdFilter?: number;
}

// TODO[Phase2]: this control is very similar to HomeScreen. So, I copy/pasted integration from there but there are some deviations in data structures. See previous UI version and figma and restore.
const TransactionsTable = (props: Props) => {
	const [expandedRows, setExpandedRows] = useState<
		DataTableExpandedRows | DataTableValueArray | undefined
	>([]);

	//const [date, setDate] = useState<Date | null>(null);

	// sorting states
	const [sort, setSort] = useState({ sortField: '', sortOrder: 0 });
	const [nameOrLocationFilter, setNameOrLocationFilter] = useState('');
	const [tableFilter, setTableFilter] = useState(''); // Used for actual table filtering

	// filtering states
	const [showFilterPanel, setShowFilterPanel] = useState(false);
	const [paginationFilter, setPaginationFilter] = useState({ Skip: 0, Take: 5 });
	const [dateFilter, setDateFilter] = useState<moment.Moment | null>(null);
	const [statusFilter, setStatusFilter] = useState<null | string>(null);
	const [programsCheckedIds, setProgramsCheckedIds] = useState<null | number[]>(null);
	const [amountFilter, setAmountFilter] = useState<null | number>(0);
	// Currently not used because we apply filters immediately
	const [filterList, setSideFilter] = useState<string[]>([]);
	const [emptyState, setEmptyState] = useState<'noData' | 'noSearchData' | 'noFilterData'>('noData');
	//const emptyState = 'noActiveRides';



	const programsQ = useQuery({
		queryKey: ['Home.Programs'],
		queryFn: async () => {
			const result = await axiosInstance.post<GenericResponse<ListResponse<X4bProgramDto>>>(ENDPOINTS['/api/X4bProgram/GetProgramList'], {});
			setProgramsCheckedIds(result.data.Data.Items.map(i => {
				return i.Id;
			}));
			return result.data.Data;
		}
	});
	const makeListRequest = () => {
		return {
			Skip: paginationFilter.Skip,
			Take: paginationFilter.Take,
			SortField: sort.sortField,
			SortOrder: sort.sortOrder,
			Filters: [{ Name: "NameOrLocation", Value: tableFilter }],
			// Without toDate here we have the post request never finishing in debugger. Crazy. AI failed to explain it. It sayd that it was causing rapid query cancellation which typically is not logged by browsers. I don't know how to verify that.
			Date: DateHelper.prepareDateToStore(dateFilter?.toDate()),
			// Backend expects string[]; selecting "Pending" also fetches RESERVED rows.
			Status: statusFilter ? expandUiStatusesForFilter([statusFilter as PaymentStatusUi]) : null,
			ProgramIds: programsCheckedIds,
			Amount: amountFilter
		} as GetPaymentListRequest;
	}
	const activitiesQ = useQuery({
		queryKey: ['Transactions.GetPaymentList', paginationFilter, sort, tableFilter, dateFilter?.toISOString(), statusFilter, programsCheckedIds, amountFilter],
		queryFn: async () => {
			const result = await axiosInstance.post<GenericResponse<ListResponse<X4bPaymentDto>>>(ENDPOINTS['/api/payments/GetPaymentList'], makeListRequest());
			return result.data.Data;
		},
		enabled: programsQ.isSuccess,
	});
	const toggleFilterPanel = () => {
		setShowFilterPanel(!showFilterPanel);
	};

	const handlePaymentSelect = (text: string) => {
		setPaginationFilter({ Skip: 0, Take: paginationFilter.Take });
		// Update the table filter to trigger a search
		setTableFilter(text);
	};

	const handleProgramCheckboxChange = (id: number) => {
		if (!programsCheckedIds) setProgramsCheckedIds([]);
		if (programsCheckedIds!.includes(id)) {
			setProgramsCheckedIds(programsCheckedIds!.filter((checkedId) => checkedId !== id));
		} else {
			setProgramsCheckedIds([...programsCheckedIds!, id]);
		}
	};

	const isRowExpanded = (rowData: DataTableValue) =>
		Array.isArray(expandedRows)
			? expandedRows.some((row) => row === rowData)
			: false;

	const getStatusStyle = (status: PaymentStatusUi) => {
		switch (status) {
			case 'Succeeded':
				return styles.statusCompleted;
			case 'Failed':
				return styles.statusCancelled;
			case 'Pending':
				return styles.statusOnTrip;
			case 'Canceled':
				return styles.statusDefault;
		}
	};
	const onRowsChange = (rows: number) => {
		setPaginationFilter(copyWith(paginationFilter, { Skip: 0, Take: rows }));
	}
	const renderEmptyState = (state?: 'noData' | 'noSearchData' | 'noFilterData') => {
		switch (state) {
			case 'noData':
				return EmptyTable({
					title: 'There are no active rides right now',
					src: '/images/svg/empty-state-no-active-rides.svg',
				});
			case 'noSearchData':
				return EmptyTable({
					title: 'We didn’t find a match',
					description: 'Try searching for something else.',
					src: '/images/svg/no-result.svg',
					descriptionClassName: 'text-primaryContainer',
				})
			case 'noFilterData':
				return EmptyTable({
					title: 'We didn’t find a match',
					description: 'Try searching for something else.',
					src: '/images/svg/no-result.svg',
					descriptionClassName: 'text-primaryContainer',
				})
			// case 'error':
			// 	return (
			// 		<EmptyState title="Something went wrong" variant="triangle" />
			// 	);
			default:
				return null;
		}
	};


	useEffect(() => {
		const hasSearch = tableFilter.trim() !== '';
		const hasFilters = (statusFilter != null) ||
			(programsCheckedIds != null && programsCheckedIds.length !== programsQ.data?.TotalRecords) ||
			(amountFilter != 0);

		if (hasFilters) setEmptyState('noFilterData');
		else if (hasSearch) setEmptyState('noSearchData');
		else setEmptyState('noData');
		//console.log(hasSearch, hasFilters);
	}, [tableFilter, statusFilter, programsCheckedIds, programsQ.data?.TotalRecords, amountFilter]);

	var sideFiltersNumber = 0;
	if (statusFilter != null) sideFiltersNumber++;
	if (programsCheckedIds != null && programsCheckedIds.length != programsQ.data?.TotalRecords) sideFiltersNumber++;
	if (amountFilter != 0) sideFiltersNumber++;

	return (
		<>
			<Toolbar
				pt={{ start: { className: 'tablet:w-full' } }}
				start={<>
					<div
						className={classNames(
							styles.toolbarContent,
							'flex flex-col md:flex-row tablet:w-full',
						)}
					>

						<ActivitySearchAutocomplete
							onSelect={handlePaymentSelect}
							placeholder="Search by name or location"
							className="w-[360px] tablet:!w-auto tablet:!flex-1 tablet:!min-w-0 h-[56px]"
							value={nameOrLocationFilter}
							onChange={setNameOrLocationFilter}
						/>
						<DateInput
							value={dateFilter}
							onChange={setDateFilter}
							variant="outlined"
							icon={(<MdToday size={24} className="text-onSurfaceVariant" />)}
							iconCls="h-10 w-10 p-2 rounded-[100px] bg-[#D5D8DB] pointer-events-none top-2 right-1"
							labelBgColor="bg-surface"
							containerCls="w-[312px] tablet:w-[220px] tablet:!flex-none"
							calendarProps={{
								minDate: moment().year(2025).startOf('year'),
								maxDate: moment().year(2025).startOf('year').add(20, 'years'),
							}}
						/>
					</div>
				</>}
				end={<div className={classNames(styles.toolbarContent)}>
					<RoundedFilledButton
						id="filter"
						className={clsx(
							"with-icon-button",
							sideFiltersNumber !== 0 && "border-2 border-solid border-orange-600"
						)}
						icon={MdFilterAlt}
						label={`Filter${sideFiltersNumber == 0 ? '' : `(${sideFiltersNumber})`}`}
						onClick={toggleFilterPanel}
						width="110px"
					/>

					<RoundedFilledButton
						id="export"
						className="with-icon-button-primary"
						icon={MdCloudDownload}
						label="Export"
						iconColor="text-onPrimary"
						onClick={async () => {
							const response = await axiosInstance.post(
								ENDPOINTS['/api/X4bActivity/ExportActivityList'],
								makeListRequest(),
								{ responseType: 'blob' }
							);
							// Create a blob from the response
							const blob = new Blob([response.data], { type: 'text/csv' });
							// Create a link element
							const url = window.URL.createObjectURL(blob);
							const link = document.createElement('a');
							link.href = url;
							link.setAttribute('download', 'activity-list.csv');
							document.body.appendChild(link);
							link.click();
							link.parentNode?.removeChild(link);
							window.URL.revokeObjectURL(url);
						}}
						width="123px"
					/>
				</div>}
				className={styles.toolbar}
			/>

			<FilterPanel
				show={showFilterPanel}
				onClose={() => setShowFilterPanel(false)}
				// Filter by payment status (matches the column). RESERVED is folded into
				// "Pending" by the payment status helper, so users see only 4 options.
				statusList={[...PAYMENT_STATUSES_UI]}
				statusSelected={statusFilter}
				setStatusSelected={setStatusFilter}
				programsCheckedIds={programsCheckedIds}
				handleProgramCheckboxChange={handleProgramCheckboxChange}
				filterList={filterList}
				setSideFilter={setSideFilter}
				programsQ={programsQ}
				amount={amountFilter}
				onAmountChange={(am: number) => {
					setAmountFilter(am);
				}}
				onFilterClear={() => {
					setStatusFilter(null);
					setProgramsCheckedIds(programsQ.data!.Items.map(i => {
						return i.Id;
					}));
					setAmountFilter(0);
				}}
			/>

			<div className="relative">
				{activitiesQ.isLoading && (
					<div className="absolute top-0 w-full rounded-t h-2">
						<ProgressBar
							className={styles.progressBar}
							mode="indeterminate"
							color="#0080FF"
							pt={{
								container: {
									className: 'bg-primary500',
								},
							}}
						/>
					</div>
				)}
				<DataTable
					value={activitiesQ.data?.Items}
					className={styles.customDatatable}
					tableStyle={{ minWidth: '50rem', width: '100%' }}
					pt={{ paginator: paginatorPassThrough }}
					paginatorTemplate={buildPaginatorTemplate(onRowsChange)}
					currentPageReportTemplate={PAGINATOR_CURRENT_REPORT}
					emptyMessage={activitiesQ.isLoading ? null : renderEmptyState(emptyState)}
					expandedRows={expandedRows}
					onRowToggle={(e) => setExpandedRows(e.data)}
					rowExpansionTemplate={(rowData: X4bPaymentDto) => rowData.Activity ? <RowExpansionTemplate data={rowData.Activity} /> : null}
					loading={activitiesQ.isLoading}
					dataKey="Id"
					paginator={(activitiesQ.data?.Items?.length ?? 0) > 0}
					first={paginationFilter.Skip}
					rows={paginationFilter.Take}
					lazy
					totalRecords={activitiesQ.data?.TotalRecords}
					rowsPerPageOptions={[5, 10, 25, 50]}
					onPage={(event: DataTableStateEvent) => {
						setPaginationFilter(copyWith(paginationFilter, { Skip: event.first }));
					}}
					// onRowsChange={(rows) => {
					// 	setPaginationFilter(copyWith(paginationFilter, { Skip: 0, Take: rows }));
					// }}
					sortField={sort.sortField}
					sortOrder={sort.sortOrder as SortOrder}
					onSort={(e: DataTableStateEvent) => {
						setSort({
							sortField: e.sortField,
							sortOrder: e.sortOrder!,
						});
					}}
				//{...rest} // <-- Spread the rest of the props here
				>
					<Column
						header=""
						body={(rowData) =>
							activitiesQ.isLoading ? null : (
								<button
									type="button"
									onClick={() =>
										setExpandedRows((prev) => {
											const prevArray = Array.isArray(prev)
												? prev
												: [];
											return isRowExpanded(rowData)
												? prevArray.filter(
													(row) => row !== rowData,
												)
												: [...prevArray, rowData];
										})
									}
									className={styles.expandToggle}
								>
									{isRowExpanded(rowData) ? (
										<MdArrowDropDown
											size={24}
											className="text-tertiary"
										/>
									) : (
										<MdArrowRight
											size={24}
											className="text-black"
										/>
									)}
								</button>
							)
						}
						style={{ width: '3rem', textAlign: 'center' }}
					/>
					<Column field="Date" header="Date" sortable
						body={(payment: X4bPaymentDto) => payment.Date ? DateHelper.formatDate(new Date(payment.Date)) : ''}
					/>
					<Column field="CreatorNameFull" header="Name" sortable />
					<Column field="Location" header="Location" sortable />
					<Column field="ProgramName" header="Program" sortable />
					<Column field="Amount" header="Amount" sortable
						body={(payment: X4bPaymentDto) => {
							return <span>
								{formatCurrency(payment.Amount ?? 0)}
							</span>
						}}
					/>
					<Column field="Status" header="Status" sortable
						body={(payment: X4bPaymentDto) => {
							const ui = convertPaymentStatus(payment.Status);
							return <span
								className={`${styles.statusBadge} ${getStatusStyle(ui)}`}>
								{ui}
							</span>
						}}
					/>
				</DataTable>
			</div>
		</>
	);
};

export default TransactionsTable;
